import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Fetch, baseUrl } from "../../shared/Fetch";

interface LeadDetailDialogProps {
  visible: boolean;
  onHide: () => void;
  loadingDetails: boolean;
  leadDetails: any;
  onToggleActivity: (leadId: number, emailSent?: boolean, callMade?: boolean) => void;
  setLeadDetails: React.Dispatch<React.SetStateAction<any>>;
}

export const LeadDetailDialog: React.FC<LeadDetailDialogProps> = ({
  visible,
  onHide,
  loadingDetails,
  leadDetails,
  onToggleActivity,
  setLeadDetails,
}) => {
  const [emailHistoryList, setEmailHistoryList] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (visible && leadDetails?.id) {
      loadEmailHistory(leadDetails.id);
    } else {
      setEmailHistoryList([]);
    }
  }, [visible, leadDetails?.id]);

  const loadEmailHistory = async (leadId: number) => {
    setLoadingHistory(true);
    try {
      const res = await Fetch(`LeadAllocation/my-leads/${leadId}/email-history`, "GET", null, null, null);
      if (res) {
        const data = await res.json();
        setEmailHistoryList(data || []);
      }
    } catch (err) {
      console.error("Failed to load email history", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const getAttachmentUrl = (filename: string) => {
    const rootUrl = baseUrl.replace("/api/", "/");
    return `${rootUrl}uploads/${filename}`;
  };
  const getFriendlyFileName = (filename: string) => {
    const parts = filename.split("_");
    if (parts.length > 1) {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidRegex.test(parts[0])) {
        return parts.slice(1).join("_");
      }
    }
    return filename;
  };
  const stripHtml = (html?: string) => {
    if (!html) return "-";

    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };
  return (
    <Dialog
      header="Lead Details"
      visible={visible}
      style={{ width: "800px" }}
      onHide={onHide}
      modal
      className="p-fluid"
    >
      {loadingDetails ? (
        <div className="flex justify-content-center align-items-center py-5">
          <i className="pi pi-spin pi-spinner text-primary text-3xl"></i>
        </div>
      ) : leadDetails ? (
        <div className="flex flex-column gap-4 p-2">
          <div className="flex align-items-center justify-content-between p-3 bg-bluegray-50 border-round">
            <div>
              <h6
                className="m-0 text-bluegray-500 font-semibold"
                style={{ fontSize: "0.8rem", textTransform: "uppercase" }}
              >
                Lead ID: #{leadDetails.id}
              </h6>
              <h4 className="m-0 mt-1 text-900 font-bold">
                {leadDetails.contact_Person?.contact_Person_Name || "N/A"}
              </h4>
            </div>
            {/* <span className="p-badge p-badge-info p-badge-lg px-3 py-2 font-semibold">
              {leadDetails.status?.status_Name || "New"}
            </span> */}
          </div>

          <div>
            <span className="block text-primary font-semibold mb-3 border-bottom-1 surface-border pb-1">
              <i className="pi pi-user mr-2"></i>Contact Information
            </span>
            <div className="grid">
              <div className="col-12 md:col-6 mb-3">
                <span className="text-sm text-color-secondary block mb-1">
                  Email Address
                </span>
                <span className="font-semibold text-800 text-break">
                  {leadDetails.contact_Person?.contact_Person_Email || "N/A"}
                </span>
              </div>
              <div className="col-12 md:col-6 mb-3">
                <span className="text-sm text-color-secondary block mb-1">
                  Phone Number
                </span>
                <span className="font-semibold text-800">
                  {leadDetails.contact_Person?.contact_Person_Number || "N/A"}
                </span>
              </div>
              <div className="col-12 md:col-6 mb-3">
                <span className="text-sm text-color-secondary block mb-1">
                  Lead Source
                </span>
                <span className="font-semibold text-800">
                  {leadDetails.contact_Person?.source?.source_Name || "N/A"}                </span>
              </div>
              <div className="col-12 md:col-6 mb-3">
                <span className="text-sm text-color-secondary block mb-1">
                  Allocated On
                </span>
                <span className="font-semibold text-800">
                  {leadDetails.createdAt
                    ? new Date(leadDetails.createdAt).toLocaleDateString()
                    : "N/A"}
                </span>
              </div>
            </div>
          </div>



          <div>
            <span className="block text-primary font-semibold mb-3 border-bottom-1 surface-border pb-1">
              <i className="pi pi-check-square mr-2"></i>Actions / Activities
            </span>
            <div className="flex flex-column gap-3 p-3 bg-highlight-50 border-round border-1 border-primary-200">
              <div className="flex flex-column gap-2">
                <div className="flex justify-content-between align-items-center">
                  <div className="flex align-items-center gap-2">
                    <i className="pi pi-envelope text-primary text-xl"></i>
                    <div>
                      <span className="font-medium block">Email Communication</span>
                      <small className="text-color-secondary">Has an email been sent to this lead?</small>
                    </div>
                  </div>
                  <span className={`font-semibold px-3 py-1 border-round text-sm ${leadDetails.emailSent ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {leadDetails.emailSent ? "Yes" : "No"}
                  </span>
                </div>
                {loadingHistory ? (
                  <div className="text-center py-3">
                    <i className="pi pi-spin pi-spinner text-primary text-sm"></i>
                  </div>
                ) : emailHistoryList && emailHistoryList.length > 0 ? (
                  <div className="flex flex-column gap-3 mt-1">
                    {emailHistoryList.map((email, index) => (
                      <div
                        key={email.id || index}
                        className="p-3 bg-white border-round border-1 border-gray-200 text-sm text-700 flex flex-column gap-2"
                      >
                        <div className="flex justify-content-between align-items-center border-bottom-1 surface-border pb-1">
                          <span className="font-semibold text-800">
                            Subject: {email.subject}
                          </span>
                          <span className="text-xs text-color-secondary font-medium">
                            {email.createdAt
                              ? new Date(email.createdAt).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                // hour: "2-digit",
                                // minute: "2-digit",
                                // hour12: true,
                              })
                              : "-"}
                          </span>
                        </div>

                        <div className="text-sm text-600">
                          <span className="font-semibold text-800 block mb-1">Description:</span>
                          <div
                            className="p-2 border-round bg-gray-50 text-800 overflow-y-auto max-h-150"
                            style={{ minHeight: "40px" }}
                            dangerouslySetInnerHTML={{ __html: leadDetails.emailDescription || "<p>-</p>" }}
                          />
                        </div>

                        {email.attachments && (
                          <div className="flex flex-column gap-1 mt-1">
                            <span className="font-semibold text-800 text-xs">Attachments:</span>
                            <div className="flex flex-wrap gap-2">
                              {email.attachments.split(",").map((file: string, idx: number) => {
                                const filename = file.trim();
                                if (!filename) return null;
                                return (
                                  <a
                                    key={idx}
                                    href={`${baseUrl}LeadAllocation/download-attachment/${filename}`}
                                    download={getFriendlyFileName(filename)}
                                    className="px-2 py-1 text-xs bg-blue-50 text-blue-700 border-1 border-blue-200 border-round flex align-items-center gap-1 no-underline font-medium hover:bg-blue-100 transition-colors"
                                  >
                                    <i className="pi pi-download text-xs" />
                                    {getFriendlyFileName(filename)}
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-1 p-2 bg-white border-round border-1 border-gray-200 text-sm text-700 text-center text-color-secondary">
                    No email communication sent yet.
                  </div>
                )}
              </div>
              <hr className="m-0 surface-border" />
              <div className="flex flex-column gap-2">
                <div className="flex justify-content-between align-items-center">
                  <div className="flex align-items-center gap-2">
                    <i className="pi pi-phone text-primary text-xl"></i>
                    <div>
                      <span className="font-medium block">Phone Call</span>
                      <small className="text-color-secondary">Has a call been completed with this lead?</small>
                    </div>
                  </div>
                  <span className={`font-semibold px-3 py-1 border-round text-sm ${leadDetails.callMade ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {leadDetails.callMade ? "Yes" : "No"}
                  </span>
                </div>
                <div className="mt-1 p-2 bg-white border-round border-1 border-gray-200 text-sm text-700">
                  <div className="mb-2">
                    <span className="font-semibold text-800">Call Description:</span>{" "}
                    {stripHtml(leadDetails.callDescription)}
                  </div>

                  <div>
                    <span className="font-semibold text-800">Call Done At:</span>{" "}
                    {leadDetails.callMadetAt
                      ? new Date(leadDetails.callMadetAt).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        // hour: "2-digit",
                        // minute: "2-digit",
                        // hour12: true,
                      })
                      : "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-content-end mt-2 pt-2 border-top-1 surface-border">
            <Button
              label="Close"
              className="p-button-secondary p-button-sm p-button-rounded"
              style={{ width: "100px" }}
              onClick={onHide}
            />
          </div>
        </div>
      ) : (
        <div className="text-center py-4 text-color-secondary">Failed to load lead details.</div>
      )}
    </Dialog>
  );
};
